import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../App';
import { FolderPlus, Plus, Check, X } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AddToCollectionButton({ materialId, materialName, className = '' }) {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(null);
  
  useEffect(() => {
    if (isOpen && user) {
      fetchCollections();
    }
  }, [isOpen, user]);
  
  const fetchCollections = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/collections/user/list`, { withCredentials: true });
      setCollections(response.data.collections || []);
    } catch (error) {
      console.error('Error fetching collections:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleAddToCollection = async (collectionId, collectionName) => {
    setAdding(collectionId);
    try {
      await axios.post(`${API}/collections/${collectionId}/items`, {
        item_type: 'material',
        material_id: materialId
      }, { withCredentials: true });
      toast.success(`Aggiunto a "${collectionName}"`);
      setIsOpen(false);
    } catch (error) {
      if (error.response?.data?.message?.includes('already')) {
        toast.info('Già presente nella collezione');
      } else {
        toast.error('Errore');
      }
    } finally {
      setAdding(null);
    }
  };
  
  if (!user) return null;
  
  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 ${className}`}
        title="Aggiungi a collezione"
        data-testid={`add-to-collection-btn-${materialId}`}
      >
        <FolderPlus className="w-4 h-4" />
        <span className="hidden sm:inline">Collezione</span>
      </button>
      
      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 top-full mt-2 w-64 bg-white border border-neutral-200 shadow-lg z-50">
            <div className="p-3 border-b border-neutral-100">
              <div className="flex items-center justify-between">
                <span className="font-medium text-sm">Aggiungi a collezione</span>
                <button onClick={() => setIsOpen(false)}>
                  <X className="w-4 h-4 text-neutral-400" />
                </button>
              </div>
            </div>
            
            <div className="max-h-64 overflow-y-auto">
              {loading ? (
                <div className="p-4 text-center text-neutral-500 text-sm">
                  Caricamento...
                </div>
              ) : collections.length === 0 ? (
                <div className="p-4 text-center">
                  <p className="text-neutral-500 text-sm mb-2">Nessuna collezione</p>
                  <a
                    href="/collections"
                    className="text-sm text-neutral-900 underline"
                  >
                    Crea collezione
                  </a>
                </div>
              ) : (
                <div className="py-2">
                  {collections.map(collection => (
                    <button
                      key={collection.collection_id}
                      onClick={() => handleAddToCollection(collection.collection_id, collection.name)}
                      disabled={adding === collection.collection_id}
                      className="w-full text-left px-4 py-2 hover:bg-neutral-50 flex items-center justify-between disabled:opacity-50"
                    >
                      <div>
                        <p className="text-sm font-medium text-neutral-900">{collection.name}</p>
                        <p className="text-xs text-neutral-500">{collection.materials_count} materiali</p>
                      </div>
                      {adding === collection.collection_id ? (
                        <div className="w-4 h-4 border-2 border-neutral-400 border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Plus className="w-4 h-4 text-neutral-400" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            <div className="p-2 border-t border-neutral-100">
              <a
                href="/collections"
                className="flex items-center justify-center gap-2 w-full py-2 text-sm text-neutral-600 hover:text-neutral-900 hover:bg-neutral-50"
              >
                <Plus className="w-4 h-4" />
                Nuova Collezione
              </a>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
